import { array, boolean, discriminatedUnion, enum as enumFactory, literal, number, record, strictObject, string, url } from "../../lazy-zod.js";
//#region src/schemas/http/connector-connections.ts
const CENTER_AGENT_APP_CONNECTOR_SERVICES = [
	"feishu",
	"lark",
	"gmail",
	"googlecalendar",
	"googledrive",
	"notion",
	"linear",
	"figma",
	"posthog",
	"cloudflare_browser_rendering",
	"cloudflare_dns",
	"cloudflare_r2",
	"cloudflare_worker"
];
const CONNECTOR_SERVICES = ["github", ...CENTER_AGENT_APP_CONNECTOR_SERVICES];
const CONNECTOR_CONNECTION_STATUSES = [
	"pending",
	"active",
	"disconnected"
];
const CONNECTOR_CONNECTION_SCOPES = ["member", "shared"];
const CONNECTOR_CONNECTION_PREFERENCES = [
	"automatic",
	"requester",
	"shared"
];
const CONNECTOR_APP_REQUEST_SOURCES = ["openconnector_catalog", "freeform"];
const CONNECTOR_APP_REQUEST_STATUSES = [
	"requested",
	"available",
	"declined"
];
function buildConnectorAppRequestProviderKey(input) {
	const providerId = input.providerId?.trim().toLowerCase();
	if (input.source === "openconnector_catalog" && providerId) return `catalog:${providerId}`;
	return `freeform:${input.providerName.normalize("NFKC").trim().toLowerCase().replace(/\s+/gu, " ")}`;
}
const trimmedNonEmptyStringSchema = string().trim().min(1);
const timestampMsSchema = number().int().nonnegative();
const connectorCredentialValueSchema = string().min(1).max(8192);
const connectorAppRequestOptionalTextSchema = string().trim().min(1).max(2e3).nullable();
const connectorAppRequestedCapabilitiesSchema = array(string().trim().min(1).max(80)).max(12);
const connectorServiceSchema = enumFactory(CONNECTOR_SERVICES);
const connectorConnectionStatusSchema = enumFactory(CONNECTOR_CONNECTION_STATUSES);
const connectorConnectionScopeSchema = enumFactory(CONNECTOR_CONNECTION_SCOPES);
const connectorConnectionPreferenceSchema = enumFactory(CONNECTOR_CONNECTION_PREFERENCES);
const connectorAppRequestSourceSchema = enumFactory(CONNECTOR_APP_REQUEST_SOURCES);
const connectorAppRequestStatusSchema = enumFactory(CONNECTOR_APP_REQUEST_STATUSES);
const connectorConnectionNameSchema = string().regex(/^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$/u);
const githubAppInstallationStateSchema = string().regex(/^githubapp_[0-9a-f]{64}$/u);
const githubAppInstallationIdSchema = string().regex(/^[1-9][0-9]*$/u);
const connectorConnectionSchema = strictObject({
	canDisconnect: boolean(),
	connectionName: connectorConnectionNameSchema,
	createdAtMs: timestampMsSchema,
	disconnectedAtMs: timestampMsSchema.nullable(),
	organizationId: trimmedNonEmptyStringSchema,
	ownerDisplayName: trimmedNonEmptyStringSchema,
	scope: connectorConnectionScopeSchema.default("member"),
	service: connectorServiceSchema,
	status: connectorConnectionStatusSchema,
	updatedAtMs: timestampMsSchema,
	userId: trimmedNonEmptyStringSchema
});
const connectorConnectionListResponseSchema = strictObject({
	connections: array(connectorConnectionSchema),
	currentUserId: trimmedNonEmptyStringSchema,
	organizationId: trimmedNonEmptyStringSchema
});
const connectorAppRequestSchema = strictObject({
	availableAtMs: timestampMsSchema.nullable(),
	createdAtMs: timestampMsSchema,
	id: string().trim().min(1).max(128),
	notifiedAtMs: timestampMsSchema.nullable(),
	organizationId: trimmedNonEmptyStringSchema,
	providerId: string().trim().min(1).max(160).nullable(),
	providerName: string().trim().min(1).max(120),
	requestedCapabilities: connectorAppRequestedCapabilitiesSchema,
	requesterMemberId: trimmedNonEmptyStringSchema.nullable(),
	requesterUserId: trimmedNonEmptyStringSchema.nullable(),
	source: connectorAppRequestSourceSchema,
	status: connectorAppRequestStatusSchema,
	updatedAtMs: timestampMsSchema,
	useCase: connectorAppRequestOptionalTextSchema
});
const createConnectorAppRequestBodySchema = strictObject({
	providerId: string().trim().min(1).max(160).nullable().optional(),
	providerName: string().trim().min(1).max(120),
	requestedCapabilities: connectorAppRequestedCapabilitiesSchema.optional(),
	source: connectorAppRequestSourceSchema,
	useCase: connectorAppRequestOptionalTextSchema.optional()
}).superRefine((input, context) => {
	if (input.source === "openconnector_catalog" && !input.providerId?.trim()) context.addIssue({
		code: "custom",
		message: "A catalog Plugin request requires its OpenConnector provider ID.",
		path: ["providerId"]
	});
	if (input.source === "freeform" && input.providerId != null) context.addIssue({
		code: "custom",
		message: "A freeform Plugin request cannot include a provider ID.",
		path: ["providerId"]
	});
});
const connectorAppRequestResponseSchema = strictObject({ request: connectorAppRequestSchema });
const connectorAppRequestListResponseSchema = strictObject({
	currentUserId: trimmedNonEmptyStringSchema,
	organizationId: trimmedNonEmptyStringSchema,
	requests: array(connectorAppRequestSchema).max(500)
});
const connectorAppCatalogProviderSchema = strictObject({
	authTypes: array(string().trim().min(1).max(80)).max(12),
	categories: array(strictObject({
		displayName: string().trim().min(1).max(120),
		id: string().trim().min(1).max(120)
	})).max(12),
	displayName: string().trim().min(1).max(120),
	homepageUrl: url().nullable(),
	iconUrl: url().nullable(),
	service: string().trim().min(1).max(160)
});
const connectorAppCatalogResponseSchema = strictObject({
	organizationId: trimmedNonEmptyStringSchema,
	providers: array(connectorAppCatalogProviderSchema).max(5e3)
});
const connectorAuthorizationStartResponseSchema = strictObject({
	authorizationUrl: url(),
	connection: connectorConnectionSchema,
	expiresAtMs: timestampMsSchema.optional()
});
const githubAppInstallationStartResponseSchema = strictObject({
	connection: connectorConnectionSchema,
	expiresAtMs: timestampMsSchema,
	state: githubAppInstallationStateSchema
});
const githubAppInstallationSetupQuerySchema = strictObject({
	installation_id: githubAppInstallationIdSchema,
	setup_action: string().trim().min(1).max(64).optional(),
	state: githubAppInstallationStateSchema
});
const githubAppInstallationVerificationResponseSchema = discriminatedUnion("status", [
	strictObject({
		actorProfileId: trimmedNonEmptyStringSchema,
		installationId: githubAppInstallationIdSchema,
		organizationId: trimmedNonEmptyStringSchema,
		status: literal("valid"),
		targetConnectionName: connectorConnectionNameSchema,
		userId: trimmedNonEmptyStringSchema,
		verificationConnectionName: connectorConnectionNameSchema
	}),
	strictObject({ status: literal("expired") }),
	strictObject({ status: literal("invalid") })
]);
const pendingGitHubAppInstallationSchema = strictObject({
	installationId: githubAppInstallationIdSchema,
	organizationId: trimmedNonEmptyStringSchema,
	status: literal("verifying"),
	targetConnectionName: connectorConnectionNameSchema,
	userId: trimmedNonEmptyStringSchema,
	verificationConnectionName: connectorConnectionNameSchema
}).nullable();
const connectorCredentialAuthTypeSchema = enumFactory(["api_key", "custom_credential"]);
const connectorCredentialConnectBodySchema = strictObject({
	authType: connectorCredentialAuthTypeSchema,
	values: record(string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/u), connectorCredentialValueSchema).refine((values) => Object.keys(values).length > 0, { message: "At least one credential value is required." }).refine((values) => Object.keys(values).length <= 16, { message: "Too many credential values." })
});
const connectorCredentialConnectResponseSchema = strictObject({ connection: connectorConnectionSchema });
const connectorOrganizationContextQuerySchema = strictObject({
	expectedOrganizationId: trimmedNonEmptyStringSchema,
	includeAuthorizationExpiry: literal("true").optional()
});
const connectorDisconnectBodySchema = strictObject({ userId: trimmedNonEmptyStringSchema });
const connectorDisconnectResponseSchema = strictObject({ connection: connectorConnectionSchema });
const connectorConnectionReconcileInputSchema = strictObject({
	availableConnectionNames: array(connectorConnectionNameSchema).max(500),
	service: connectorServiceSchema
});
const centerAgentConnectorConnectionSelectionSchema = strictObject({
	permission: enumFactory(["read", "write"]).optional(),
	connection: connectorConnectionSchema.nullable(),
	selection: enumFactory(["requester", "shared"]).nullable()
});
const centerAgentResolveConnectorConnectionBodySchema = strictObject({
	connectionPreference: connectorConnectionPreferenceSchema.optional(),
	includeAccessPolicy: literal(true).optional(),
	requesterUserId: string().nullable(),
	service: connectorServiceSchema
});
//#endregion
export { CENTER_AGENT_APP_CONNECTOR_SERVICES, CONNECTOR_APP_REQUEST_SOURCES, CONNECTOR_APP_REQUEST_STATUSES, CONNECTOR_CONNECTION_PREFERENCES, CONNECTOR_CONNECTION_SCOPES, CONNECTOR_CONNECTION_STATUSES, CONNECTOR_SERVICES, buildConnectorAppRequestProviderKey, centerAgentConnectorConnectionSelectionSchema, centerAgentResolveConnectorConnectionBodySchema, connectorAppCatalogProviderSchema, connectorAppCatalogResponseSchema, connectorAppRequestListResponseSchema, connectorAppRequestResponseSchema, connectorAppRequestSchema, connectorAppRequestSourceSchema, connectorAppRequestStatusSchema, connectorAuthorizationStartResponseSchema, connectorConnectionListResponseSchema, connectorConnectionNameSchema, connectorConnectionPreferenceSchema, connectorConnectionReconcileInputSchema, connectorConnectionSchema, connectorConnectionScopeSchema, connectorConnectionStatusSchema, connectorCredentialAuthTypeSchema, connectorCredentialConnectBodySchema, connectorCredentialConnectResponseSchema, connectorDisconnectBodySchema, connectorDisconnectResponseSchema, connectorOrganizationContextQuerySchema, connectorServiceSchema, createConnectorAppRequestBodySchema, githubAppInstallationIdSchema, githubAppInstallationSetupQuerySchema, githubAppInstallationStartResponseSchema, githubAppInstallationStateSchema, githubAppInstallationVerificationResponseSchema, pendingGitHubAppInstallationSchema };

//# sourceMappingURL=connector-connections.js.map